import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useEffect, useRef, useState } from 'react';
import {
    creaFuenteSiniestro,
    modificaFuenteSiniestro
} from '../../services/Siniestro/SiniestroService';
import {
    subirArchivosFuente,
    listarArchivosFuente,
    anularArchivoFuente,
    descargarOAbrirArchivo
} from '../../services/Siniestro/SiniestroFuenteArchivoService';
import { getInputValue, setInputValue } from '../../util/refUtils';

const MAX_ARCHIVOS = 20;
const MAX_BYTES = 50 * 1024 * 1024;
const ACCEPT_MIME = 'image/*,application/pdf';

const AdicionFuente = ({ siniestroDat, toast, funcCerrar, fuenteDat }) => {
    const refNombre = useRef('');
    const refDescripcion = useRef('');
    const refLink = useRef('');
    const fileUploadRef = useRef(null);

    const [cargando, setCargando] = useState(false);
    // ID de la fuente en edicion. Si era nueva, se setea tras Guardar.
    const [fuenteId, setFuenteId] = useState(fuenteDat?.siniestroFuenteId || null);
    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendo, setSubiendo] = useState(false);

    useEffect(() => {
        const iniciaDatos = () => {
            if (fuenteDat?.siniestroFuenteId) {
                setInputValue(refNombre, fuenteDat?.fuente);
                setInputValue(refDescripcion, fuenteDat?.descripcion);
                setInputValue(refLink, fuenteDat?.link);
            }
        };
        iniciaDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fuenteId) recargarArchivos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fuenteId]);

    const recargarArchivos = async () => {
        try {
            setCargandoArchivos(true);
            const lst = await listarArchivosFuente(fuenteId);
            setArchivos(lst || []);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los archivos' });
        } finally {
            setCargandoArchivos(false);
        }
    };

    const handleGuardarFuente = async () => {
        const nombreVal = getInputValue(refNombre);
        const descripVal = getInputValue(refDescripcion);
        const linkVal = getInputValue(refLink);
        if (!nombreVal) {
            toast.current.show({ severity: 'warn', summary: 'Atencion', detail: 'Debe llenar el campo Nombre.' });
            return;
        }
        if (!descripVal) {
            toast.current.show({ severity: 'warn', summary: 'Atencion', detail: 'Debe llenar el campo Descripcion.' });
            return;
        }
        try {
            setCargando(true);
            const objPost = {
                siniestroId: siniestroDat.siniestroId,
                fuente: nombreVal,
                descripcion: descripVal,
                link: linkVal,
                estadoConfigId: 0
            };

            let datos;
            if (fuenteId) {
                objPost['siniestroFuenteId'] = fuenteId;
                datos = await modificaFuenteSiniestro(objPost);
                if (datos.siniestroFuenteId) {
                    toast.current.show({ severity: 'success', summary: 'Exito', detail: 'Fuente actualizada.' });
                }
            } else {
                datos = await creaFuenteSiniestro(objPost);
                if (datos.siniestroFuenteId) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Exito',
                        detail: 'Fuente creada. Ahora puede adjuntar archivos.'
                    });
                    // Mantener el dialog abierto y habilitar la seccion de adjuntos.
                    setFuenteId(datos.siniestroFuenteId);
                }
            }
        } catch (error) {
            console.error('Error :', error.message);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se ha podido registrar la Fuente.' });
        } finally {
            setCargando(false);
        }
    };

    const validarSeleccion = (files) => {
        if (!files || files.length === 0) return false;
        if (archivos.length + files.length > MAX_ARCHIVOS) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Limite excedido',
                detail: `Maximo ${MAX_ARCHIVOS} archivos por fuente. Actuales: ${archivos.length}.`
            });
            return false;
        }
        for (const f of files) {
            const mime = (f.type || '').toLowerCase();
            const ok = mime.startsWith('image/') || mime === 'application/pdf';
            if (!ok) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Tipo no permitido',
                    detail: `${f.name}: solo imagenes o PDF.`
                });
                return false;
            }
            if (f.size > MAX_BYTES) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Archivo muy grande',
                    detail: `${f.name}: maximo 50 MB.`
                });
                return false;
            }
        }
        return true;
    };

    const onSeleccionArchivos = async (event) => {
        const files = event.files;
        if (!validarSeleccion(files)) {
            fileUploadRef.current?.clear();
            return;
        }
        try {
            setSubiendo(true);
            await subirArchivosFuente(fuenteId, files);
            toast.current?.show({
                severity: 'success',
                summary: 'Listo',
                detail: `${files.length} archivo(s) cargado(s).`
            });
            fileUploadRef.current?.clear();
            recargarArchivos();
        } catch (error) {
            const detalle = error?.response?.data && typeof error.response.data === 'string'
                ? error.response.data
                : 'No se pudieron subir los archivos';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: detalle });
        } finally {
            setSubiendo(false);
        }
    };

    const handleEliminar = (archivo) => {
        confirmDialog({
            message: `Eliminar el archivo "${archivo.nombreArchivo}"?`,
            header: 'Confirmar',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Si, eliminar',
            rejectLabel: 'Cancelar',
            accept: async () => {
                try {
                    await anularArchivoFuente(archivo.siniestroFuenteArchivoId);
                    toast.current?.show({ severity: 'success', summary: 'Eliminado', detail: 'Archivo eliminado' });
                    recargarArchivos();
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar' });
                }
            }
        });
    };

    const handleVer = async (archivo) => {
        try {
            await descargarOAbrirArchivo(archivo);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo descargar el archivo' });
        }
    };

    const fmtTamano = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const iconoPorTipo = (mime) => {
        const m = (mime || '').toLowerCase();
        if (m === 'application/pdf') return 'pi pi-file-pdf text-red-500';
        if (m.startsWith('image/')) return 'pi pi-image text-blue-500';
        return 'pi pi-file';
    };

    return (
        <div className="card">
            <ConfirmDialog />
            <div className="formgrid grid p-fluid mb-3">
                <div className="field col-12 md:col-4">
                    <label>Descripcion Evento:</label>
                </div>
                <div className="field col-12 md:col-8">
                    <span className="inline-block w-full bg-gray-100 border border-gray-300 text-gray-600 p-2 rounded-md text-base shadow-inner">
                        {siniestroDat.descripcion}
                    </span>
                </div>
                <div className="field col-12 md:col-6">
                    <label>Nombre Fuente:</label>
                    <InputText
                        ref={refNombre}
                        defaultValue=""
                        onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
                    />
                </div>
                <div className="field col-12 md:col-6">
                    <label style={{ visibility: 'hidden' }}>Texto oculto</label>
                </div>
                <div className="field col-12 md:col-12">
                    <label>Descripcion Fuente:</label>
                    <InputText
                        ref={refDescripcion}
                        defaultValue=""
                        onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
                    />
                </div>
                <div className="field col-12 md:col-12">
                    <label>Link Fuente:</label>
                    <InputText ref={refLink} defaultValue="" />
                </div>
                <div className="field col-12 md:col-8">
                    <label style={{ visibility: 'hidden' }}>Texto oculto</label>
                </div>
                <div className="field col-12 md:col-4">
                    <Button label="Guardar Fuente" icon="pi pi-save" onClick={handleGuardarFuente} loading={cargando} />
                </div>
            </div>

            <Divider />

            {/* SECCION ARCHIVOS ADJUNTOS */}
            <div className="mt-2">
                <h5 className="m-0 mb-2">
                    <i className="pi pi-paperclip mr-2"></i>
                    Archivos adjuntos
                    <span className="text-color-secondary text-sm font-normal ml-2">
                        (imagenes o PDF, max 50 MB c/u, hasta {MAX_ARCHIVOS} por fuente)
                    </span>
                </h5>

                {!fuenteId ? (
                    <div className="surface-100 p-3 border-round mt-2 text-color-secondary">
                        <i className="pi pi-info-circle mr-2"></i>
                        Guarda primero la fuente para poder adjuntar archivos.
                    </div>
                ) : (
                    <>
                        <FileUpload
                            ref={fileUploadRef}
                            name="archivos"
                            multiple
                            accept={ACCEPT_MIME}
                            maxFileSize={MAX_BYTES}
                            customUpload
                            uploadHandler={onSeleccionArchivos}
                            chooseLabel="Seleccionar"
                            uploadLabel="Subir"
                            cancelLabel="Cancelar"
                            disabled={subiendo}
                            emptyTemplate={<p className="m-0">Arrastra aqui imagenes o PDFs, o usa "Seleccionar".</p>}
                            invalidFileSizeMessageDetail="Tamano maximo 50 MB."
                            invalidFileSizeMessageSummary="Archivo muy grande:"
                        />

                        <div className="mt-3">
                            {cargandoArchivos ? (
                                <div className="flex justify-content-center my-2">
                                    <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                                </div>
                            ) : archivos.length === 0 ? (
                                <div className="text-color-secondary text-sm">No hay archivos adjuntos.</div>
                            ) : (
                                <ul className="list-none p-0 m-0">
                                    {archivos.map((a) => (
                                        <li
                                            key={a.siniestroFuenteArchivoId}
                                            className="flex align-items-center justify-content-between border-bottom-1 border-200 py-2"
                                        >
                                            <div className="flex align-items-center gap-2">
                                                <i className={iconoPorTipo(a.tipoMime)} style={{ fontSize: '1.4rem' }}></i>
                                                <div>
                                                    <div className="font-medium">{a.nombreArchivo}</div>
                                                    <div className="text-sm text-color-secondary">
                                                        {a.tipoMime} · {fmtTamano(a.tamanoBytes)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    icon="pi pi-download"
                                                    className="p-button-text p-button-rounded"
                                                    tooltip="Ver / Descargar"
                                                    onClick={() => handleVer(a)}
                                                />
                                                <Button
                                                    icon="pi pi-trash"
                                                    className="p-button-text p-button-rounded p-button-danger"
                                                    tooltip="Eliminar"
                                                    onClick={() => handleEliminar(a)}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className="flex justify-content-end mt-3">
                <Button label="Cerrar" icon="pi pi-times" className="p-button-secondary" onClick={funcCerrar} />
            </div>
        </div>
    );
};

export default AdicionFuente;
